import { useCallback } from 'react';
import {
  DISPLAY_ALL_CLASSES,
  DISPLAY_NO_SELECTION,
  DISPLAY_MULTIPLE_CLASSES,
} from './constants';
import { FIELD_NAMES } from '../BasicSettings';
import isEmpty from 'lodash/isEmpty';

export const getEnabledStudentIds = (cls, disabledStudents) => {
  return cls.students
    .filter(
      (student) =>
        !disabledStudents.some(
          (ds) => ds.studentId === student.id && ds.sectionId === cls.id,
        ),
    )
    .map((student) => student.id);
};

export const initializeSelectedClasses = (
  classes,
  disabledStudents = [],
  inProgressAssignments = [],
) => {
  let initialSelections = {};

  // Determine the selection strategy based on the context
  if (
    classes.length === 1 ||
    (inProgressAssignments.length === 0 && disabledStudents.length === 0)
  ) {
    initialSelections = selectEnabledStudents(classes, disabledStudents);
  } else {
    initialSelections = {};
  }

  return initialSelections;
};

const selectEnabledStudents = (classes, disabledStudents) => {
  const selections = {};
  classes.forEach((cls) => {
    const enabledStudentIds = getEnabledStudentIds(cls, disabledStudents);
    selections[cls.id] = { student_ids: enabledStudentIds };
  });
  return selections;
};

// Toggle selection of a class
const toggleClassSelection = (
  selectedClasses,
  classId,
  classes,
  disabledStudents,
) => {
  const classIndex = classes.findIndex((cls) => cls.id === classId);
  if (classIndex === -1) return; // Exit if class is not found

  const cls = classes[classIndex];
  const selectedClass = selectedClasses[classId];
  const studentIds = getEnabledStudentIds(cls, disabledStudents);
  const allStudentsSelected =
    selectedClass &&
    selectedClasses[classId].student_ids.length === cls.students.length;

  if (cls.students.length === 0) {
    // Toggle selection for classes with no students
    if (selectedClass && selectedClass.student_ids.length === 0) {
      delete selectedClasses[classId]; // deselect an empty class if its selected
    } else {
      selectedClasses[classId] = { student_ids: [] }; // Select the class
    }
  } else if (!selectedClass) {
    // If the class is not selected, select all students
    selectedClasses[classId] = { student_ids: studentIds };
  } else if (allStudentsSelected) {
    // If all students are already selected, deselect all students
    delete selectedClasses[classId];
  } else {
    if (
      selectedClass &&
      selectedClass.student_ids.length === studentIds.length
    ) {
      delete selectedClasses[classId];
    } else if (!allStudentsSelected) {
      selectedClasses[classId].student_ids = studentIds;
    }
  }
};

// Toggle selection of a student within a class
const toggleStudentSelection = (selectedClasses, classId, studentId) => {
  let classSelection = selectedClasses[classId];

  // Ensure the class is selected if any student within it is selected
  if (!classSelection) {
    selectedClasses[classId] = { student_ids: [] };
    classSelection = selectedClasses[classId];
  }

  const studentIndex = classSelection.student_ids.indexOf(studentId);
  if (studentIndex === -1) {
    // Add the student ID if not already selected
    classSelection.student_ids.push(studentId);
  } else {
    // Remove the student ID if already selected
    classSelection.student_ids.splice(studentIndex, 1);
    // If no students are selected, consider deselecting the class as well
    if (classSelection.student_ids.length === 0) {
      delete selectedClasses[classId];
    }
  }
};

// Check if all classes are selected
const checkAllClassesSelected = (
  selectedClasses,
  classes,
  disabledStudents,
) => {
  return classes.every((cls) => {
    const enabledStudentIds = getEnabledStudentIds(cls, disabledStudents);
    if (enabledStudentIds.length === 0) {
      // If there are no enabled students, check if the class itself is marked as selected
      return selectedClasses.hasOwnProperty(cls.id);
    } else {
      // Check if all enabled students are selected
      return (
        selectedClasses[cls.id] &&
        enabledStudentIds.every((studentId) =>
          selectedClasses[cls.id]?.student_ids.includes(studentId),
        )
      );
    }
  });
};

// Select all classes and their students
const selectAllClasses = (selectedClasses, classes, disabledStudents) => {
  classes.forEach((cls) => {
    const enabledStudentIds = getEnabledStudentIds(cls, disabledStudents);
    selectedClasses[cls.id] = {
      student_ids: enabledStudentIds,
    };
  });
};

// Managing selections
export const useSelections = (
  classes,
  disabledStudents,
  alreadySelected = {},
  setValue,
  formId,
  setIsAllSelected,
  isAllSelected,
) => {
  const toggleSelection = (classId, studentId = null) => {
    const newSelectedClasses = { ...alreadySelected };

    // Toggle selection based on studentId
    if (studentId === null) {
      toggleClassSelection(
        newSelectedClasses,
        classId,
        classes,
        disabledStudents,
      );
    } else {
      toggleStudentSelection(newSelectedClasses, classId, studentId);
    }

    // Update all selection status
    const allClassesSelected = checkAllClassesSelected(
      newSelectedClasses,
      classes,
      disabledStudents,
    );
    setIsAllSelected(allClassesSelected);

    setValue(`${formId}.${FIELD_NAMES.CLASS_DROPDOWN}`, newSelectedClasses);
  };

  const selectAll = useCallback(() => {
    let newSelectedClasses = { ...alreadySelected };

    if (isAllSelected) {
      /**
       * set selected classes to empty dict, which signifies no selection i.e.
       * Unselect All
       */
      newSelectedClasses = {};
    } else {
      selectAllClasses(newSelectedClasses, classes, disabledStudents);
    }

    setIsAllSelected(!isAllSelected);

    setValue(`${formId}.${FIELD_NAMES.CLASS_DROPDOWN}`, newSelectedClasses);
  }, [isAllSelected]);

  return { toggleSelection, selectAll };
};

export const totalEnabledStudentsCount = (
  classes,
  selectedClasses,
  disabledStudents,
  allEnabled = false,
) => {
  let count = 0;

  classes.forEach((cls) => {
    if (cls.students.length === 0) {
      // Check if the class entry exists to determine if it's selected
      return selectedClasses[cls.id] !== undefined;
    } else {
      if (allEnabled || (!allEnabled && selectedClasses[cls.id])) {
        const enabledSelectedStudentIds = getEnabledStudentIds(
          cls,
          disabledStudents,
        );
        count += enabledSelectedStudentIds.length;
      }
    }
  });
  return count;
};

export const hasDisabledStudentsInSelectedClass = (cls, disabledStudents) => {
  return cls.students.some((student) =>
    disabledStudents.some(
      (ds) => ds.studentId === student.id && ds.sectionId === cls.id,
    ),
  );
};

const computeSelectedStudentsCount = (
  classes,
  selectedClasses,
  disabledStudents,
) => {
  return classes.reduce((total, cls) => {
    const selectedStudentIds = selectedClasses[cls.id]?.student_ids || [];
    const enabledSelectedStudentIds = selectedStudentIds.filter(
      (studentId) => !disabledStudents.some((ds) => ds.studentId === studentId),
    );
    return total + enabledSelectedStudentIds.length;
  }, 0);
};

const getSingleSelectedStudentName = (classes, selectedClasses) => {
  let totalSelectedStudents = 0;
  let selectedStudentId = null;

  Object.values(selectedClasses).forEach(({ student_ids }) => {
    if (student_ids.length > 0) {
      totalSelectedStudents += student_ids.length;
      if (student_ids.length === 1) {
        selectedStudentId = student_ids[0];
      }
    }
  });

  if (totalSelectedStudents === 1 && selectedStudentId) {
    const student = classes
      .flatMap((cls) => cls.students)
      .find((student) => student.id === selectedStudentId);
    return student ? `${student.firstName} ${student.lastName}` : null;
  }

  return null;
};

export const allStudentsSelectedInitally = (
  classes,
  selectedClasses,
  disabledStudents,
) => {
  if (isEmpty(selectedClasses)) {
    return {
      currentlySelectedStudentsCount: 0,
      selectedClassesStudentsCount: 0,
      isAllSelected: false,
    };
  }

  const currentlySelectedStudentsCount = computeSelectedStudentsCount(
    classes,
    selectedClasses,
    disabledStudents,
  );
  const selectedClassesStudentsCount = totalEnabledStudentsCount(
    classes,
    selectedClasses,
    disabledStudents,
    true,
  );
  return {
    currentlySelectedStudentsCount,
    selectedClassesStudentsCount,
    isAllSelected:
      currentlySelectedStudentsCount > 0 &&
      selectedClassesStudentsCount > 0 &&
      currentlySelectedStudentsCount === selectedClassesStudentsCount,
  };
};

const determineDisplayValue = (params) => {
  const {
    classProps: {
      selectedClassesStudentsCount,
      fullySelectedClasses,
      partiallySelectedClasses,
      classes,
    },
    studentProps: {
      currentlySelectedStudentsCount,
      disabledStudents,
      singleStudentName,
    },
    inProgressAssignments,
    selectedClasses,
    fullySelectedClassStudentCount,
  } = params;

  const displayStudentsCount = formatStudentCount(
    currentlySelectedStudentsCount,
  );
  const isFullySelected =
    currentlySelectedStudentsCount === selectedClassesStudentsCount;
  const hasDisabledStudents = disabledStudents.length > 0;
  const hasInProgressAssignments = inProgressAssignments.length > 0;
  const isSingleFullySelectedClass = fullySelectedClasses.length === 1;
  const hasNoStudentSelection = currentlySelectedStudentsCount === 0;
  const hasPartiallySelectedClasses = partiallySelectedClasses.length > 0;

  const fullySelectedClass = findFullySelectedClass(selectedClasses, classes);
  const singleClassName = fullySelectedClass
    ? classes.find((cls) => cls.id === fullySelectedClass).name
    : null;

  return displayedDropdownSelectionText({
    hasNoStudentSelection,
    fullySelectedClass,
    singleClassName,
    isFullySelected,
    hasDisabledStudents,
    isSingleFullySelectedClass,
    hasInProgressAssignments,
    hasPartiallySelectedClasses,
    currentlySelectedStudentsCount,
    singleStudentName,
    displayStudentsCount,
    classes,
    fullySelectedClasses,
    fullySelectedClassStudentCount,
  });
};

const formatStudentCount = (count) =>
  `${count} ${count === 1 ? 'Student' : 'Students'}`;

const findFullySelectedClass = (selectedClasses, classes) => {
  const selectedClassIds = Object.keys(selectedClasses);
  return selectedClassIds.find((classId) => {
    const cls = classes.find((c) => c.id === classId);
    return (
      cls &&
      (cls.students.length === 0 ||
        cls.students.every((student) =>
          selectedClasses[classId].student_ids.includes(student.id),
        ))
    );
  });
};

const displayedDropdownSelectionText = ({
  hasNoStudentSelection,
  fullySelectedClass,
  singleClassName,
  isFullySelected,
  hasDisabledStudents,
  isSingleFullySelectedClass,
  hasInProgressAssignments,
  hasPartiallySelectedClasses,
  currentlySelectedStudentsCount,
  singleStudentName,
  displayStudentsCount,
  classes,
  fullySelectedClasses,
  fullySelectedClassStudentCount,
}) => {
  if (hasNoStudentSelection) {
    return fullySelectedClass ? singleClassName : DISPLAY_NO_SELECTION;
  }

  if (isFullySelected) {
    if (!hasDisabledStudents) {
      if (isSingleFullySelectedClass) {
        return singleClassName;
      } else if (hasInProgressAssignments) {
        return DISPLAY_MULTIPLE_CLASSES;
      } else if (!hasPartiallySelectedClasses) {
        return fullySelectedClasses.length === classes.length
          ? DISPLAY_ALL_CLASSES
          : DISPLAY_MULTIPLE_CLASSES;
      }
      return DISPLAY_ALL_CLASSES;
    } else {
      if (hasPartiallySelectedClasses && currentlySelectedStudentsCount === 1) {
        return fullySelectedClasses.length === 1
          ? `${fullySelectedClasses[0].name} (Everyone Else)`
          : singleStudentName;
      } else if (currentlySelectedStudentsCount > 0) {
        return classes.length === 1
          ? `${classes[0].name} (Everyone Else)`
          : displayStudentsCount;
      }
    }
  } else {
    if (!hasDisabledStudents) {
      if (hasPartiallySelectedClasses && currentlySelectedStudentsCount > 0) {
        return currentlySelectedStudentsCount === 1
          ? singleStudentName
          : displayStudentsCount;
      } else if (isSingleFullySelectedClass) {
        return singleClassName;
      } else {
        return DISPLAY_MULTIPLE_CLASSES;
      }
    } else {
      if (hasPartiallySelectedClasses) {
        if (
          isSingleFullySelectedClass &&
          currentlySelectedStudentsCount === fullySelectedClassStudentCount
        ) {
          return `${fullySelectedClasses[0].name} (Everyone Else)`;
        }
        return currentlySelectedStudentsCount === 1
          ? singleStudentName
          : displayStudentsCount;
      } else {
        return `${singleClassName} (Everyone Else)`;
      }
    }
  }
};

export const computeDisplayValue = (
  classes,
  selectedClasses = {},
  disabledStudents,
  inProgressAssignments,
) => {
  const partiallySelectedClasses = classes.filter(
    (cls) =>
      selectedClasses[cls.id]?.student_ids.length > 0 &&
      selectedClasses[cls.id].student_ids.length < cls.students.length,
  );

  const currentlySelectedStudentsCount = computeSelectedStudentsCount(
    classes,
    selectedClasses,
    disabledStudents,
  );
  const selectedClassesStudentsCount = totalEnabledStudentsCount(
    classes,
    selectedClasses,
    disabledStudents,
  );
  const singleStudentName = getSingleSelectedStudentName(
    classes,
    selectedClasses,
  );

  const isAllEnabledStudentsSelected = (
    cls,
    selectedClasses,
    disabledStudents,
  ) => {
    if (cls.students.length === 0) {
      return selectedClasses.hasOwnProperty(cls.id);
    }
    const enabledStudentIds = getEnabledStudentIds(cls, disabledStudents);
    return enabledStudentIds.every((studentId) =>
      selectedClasses[cls.id]?.student_ids.includes(studentId),
    );
  };

  const getFullySelectedClasses = (
    classes,
    selectedClasses,
    disabledStudents,
  ) => {
    return classes.filter((cls) =>
      isAllEnabledStudentsSelected(cls, selectedClasses, disabledStudents),
    );
  };

  const fullySelectedClasses = getFullySelectedClasses(
    classes,
    selectedClasses,
    disabledStudents,
  );
  const fullySelectedClassStudentCount = fullySelectedClasses.reduce(
    (total, cls) => {
      const enabledStudents = cls.students.filter(
        (student) =>
          !disabledStudents.some(
            (ds) => ds.studentId === student.id && ds.sectionId === cls.id,
          ),
      );
      return total + enabledStudents.length;
    },
    0,
  );

  return determineDisplayValue({
    classProps: {
      selectedClassesStudentsCount,
      fullySelectedClasses,
      partiallySelectedClasses,
      classes,
    },
    studentProps: {
      currentlySelectedStudentsCount,
      disabledStudents,
      singleStudentName,
    },
    inProgressAssignments,
    selectedClasses,
    fullySelectedClassStudentCount,
  });
};
